import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";

import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { FirefightClip } from "./GameStory_firefights";
import { PlayerIndexMap } from "./GameStoryDialog_UPDATED";

type ErneKind = "erne" | "attempt";

export type ErneEvent = {
  rallyIndex: number;

  vulnerableShotIndex: number;
  responseShotIndex: number;

  vulnerableShotNumber: number;
  responseShotNumber: number;

  team: 0 | 1;
  playerId: number;

  kind: ErneKind;
  side: "left" | "right";

  shotIndex: number;
  shotNumber: number;

  shotStartMs: number;
  shotEndMs: number;

  contactX: number;
  contactY: number;

  outsideByFt: number;
  nearNetByFt: number;
  nearSidelineByFt: number;

  lateralMoveFt: number;
  forwardMoveFt: number;

  vulnerableBallX: number;
  vulnerableBallY: number;
  vulnerableBallZone?: string;

  isVolley: boolean;
  isPutaway: boolean;
  shotType?: string;
  strokeType?: string;
  strokeSide?: string;

  notes: string[];
};

export type ErneClip = {
  key: string;
  event: ErneEvent;
  clipStartMs: number;
  clipEndMs: number;
};

type FilterMode = "all" | "ernes" | "attempts";
type TeamFilter = "all" | 0 | 1;

function getTeamFromPlayerId(playerId: number): 0 | 1 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}



function getBallContactPosForShot(shot: any): { x: number; y: number } | null {
  const start = shot?.resulting_ball_movement?.trajectory?.start?.location;
  const x = num(start?.x);
  const y = num(start?.y);
  if (x == null || y == null) return null;
  return { x, y };
}


function getPlayerPosForShot(shot: any): { x: number; y: number } | null {
  const pid = num(shot?.player_id);
  const pos = shot?.player_positions;

  if (pid == null || !Array.isArray(pos) || !pos[pid]) return null;

  const x = num(pos[pid]?.x);
  const y = num(pos[pid]?.y);
  if (x == null || y == null) return null;

  return { x, y };
}

function sideFromX(x: number): "left" | "right" {
  return x <= 10 ? "left" : "right";
}

function getNearSidelineByFt(x: number): number {
  return Math.min(Math.abs(x - 0), Math.abs(20 - x));
}

function getOutsideByFt(x: number): number {
  if (x < 0) return -x;
  if (x > 20) return x - 20;
  return 0;
}

function isVolley(shot: any): boolean {
  if (shot?.is_volley === true) return true;

  const ballSaysVolley = shot?.resulting_ball_movement?.is_volleyed === true;
  const startZone = String(
    shot?.resulting_ball_movement?.trajectory?.start?.zone ?? "",
  ).toLowerCase();
  const shotType = String(shot?.shot_type ?? "").toLowerCase();

  if (
    ballSaysVolley &&
    (startZone === "kitchen" || startZone === "short" || shotType === "smash")
  ) {
    return true;
  }

  return false;
}

function getPrevSamePlayerContact(
  overlays: ShotNumberOverlay[],
  shotIndex: number,
  playerId: number,
): { x: number; y: number } | null {
  for (let i = shotIndex - 1; i >= 0; i -= 1) {
    const s: any = overlays[i]?.rawShot;
    if (!s) continue;
    if (Number(s.player_id) !== playerId) continue;

    const pos = s?.player_positions;
    if (!Array.isArray(pos) || !pos[playerId]) continue;

    const x = Number(pos[playerId]?.x);
    const y = Number(pos[playerId]?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    return { x, y };
  }

  return null;
}

type VulnerableBallInfo = {
  side: "left" | "right";
  endX: number;
  endY: number;
  endZone?: string;
  score: number;
  reasons: string[];
};


function getVulnerableBallInfo(shot: any): VulnerableBallInfo | null {
  const end = shot?.resulting_ball_movement?.trajectory?.end?.location;
  const endZone = String(
    shot?.resulting_ball_movement?.trajectory?.end?.zone ?? "",
  ).toLowerCase();
  const peakZ = num(shot?.resulting_ball_movement?.trajectory?.peak?.z);
  const endX = num(end?.x);
  const endY = num(end?.y);
  const shotType = String(shot?.shot_type ?? "").toLowerCase();
  const speed = num(shot?.resulting_ball_movement?.speed);

  if (endX == null || endY == null) return null;

  const nearSidelineByFt = getNearSidelineByFt(endX);
  const side = sideFromX(endX);

  let score = 0;
  const reasons: string[] = [];

  // Wide is important, but do not make it ultra-tight.
  if (nearSidelineByFt <= 2.5) {
    score += nearSidelineByFt <= 1.1 ? 3 : 2;
    reasons.push(`wide to ${side} sideline`);
  } else {
    return null;
  }

  // Kitchen/short is best, but allow some nearby balls too.
  if (endZone === "kitchen" || endZone === "short") {
    score += 3;
    reasons.push(`arrives in ${endZone} zone`);
  } else if (endY >= 13 && endY <= 31) {
    score += 1;
    reasons.push("arrives in reachable sideline attack band");
  } else {
    return null;
  }

  // Height helps, but don't require it too strongly.
  if (peakZ != null && peakZ >= 2.6) {
    score += peakZ >= 3.4 ? 2 : 1;
    reasons.push("ball height may be attackable");
  }

  // Do not hard-reject by shot type. Just weight it.
  if (shotType === "dink" || shotType === "drop") {
    score += 2;
    reasons.push(`feed is ${shotType}`);
  } else if (shotType === "drive") {
    score += 1;
    reasons.push("feed is drive");
  } else if (shotType) {
    score += 0.25;
    reasons.push(`feed is ${shotType}`);
  }

  // Mild penalty for very hard balls, but not enough to kill everything.
  if (speed != null && speed > 30) {
    score -= 1;
    reasons.push("feed may be hard / less classic Erne bait");
  }

  if (score < 4.5) return null;

  return {
    side,
    endX,
    endY,
    endZone,
    score,
    reasons,
  };
}

function buildErneNotes(args: {
  kind: ErneKind;
  x: number;
  y: number;
  nearSidelineByFt: number;
  outsideByFt: number;
  lateralMoveFt: number;
  forwardMoveFt: number;
  vulnerableInfo: VulnerableBallInfo;
  shot: any;
}): string[] {
  const {
    kind,
    x,
    y,
    nearSidelineByFt,
    outsideByFt,
    lateralMoveFt,
    forwardMoveFt,
    vulnerableInfo,
    shot,
  } = args;

  const notes: string[] = [];

  notes.push(
    kind === "erne" ? "confirmed-ish Erne sequence" : "Erne attempt sequence",
  );
  notes.push(`contact x=${x.toFixed(1)}, y=${y.toFixed(1)}`);
  notes.push(`near sideline by ${nearSidelineByFt.toFixed(1)} ft`);
  notes.push(`lateral move ${lateralMoveFt.toFixed(1)} ft`);
  notes.push(`forward move ${forwardMoveFt.toFixed(1)} ft`);
  notes.push(
    `vulnerable ball x=${vulnerableInfo.endX.toFixed(1)}, y=${vulnerableInfo.endY.toFixed(1)}`,
  );

  if (outsideByFt > 0) {
    notes.push(`outside sideline by ${outsideByFt.toFixed(1)} ft`);
  }

  notes.push(...vulnerableInfo.reasons);

  if (shot?.is_putaway) notes.push("putaway");
  if (shot?.shot_type) notes.push(`shot_type=${shot.shot_type}`);
  if (shot?.stroke_type) notes.push(`stroke_type=${shot.stroke_type}`);
  if (shot?.stroke_side) notes.push(`stroke_side=${shot.stroke_side}`);

  return notes;
}

export function detectErneSequence(args: {
  rallyIndex: number;
  vulnerableShotIndex: number;
  overlays: ShotNumberOverlay[];
}): ErneEvent | null {
  const { rallyIndex, vulnerableShotIndex, overlays } = args;

  const vulnerableShot: any = overlays[vulnerableShotIndex]?.rawShot;
  const responseShot: any = overlays[vulnerableShotIndex + 1]?.rawShot;

  if (!vulnerableShot || !responseShot) return null;

  const vulnerablePid = num(vulnerableShot?.player_id);
  const responsePid = num(responseShot?.player_id);
  if (vulnerablePid == null || responsePid == null) return null;

  const vulnerableTeam = getTeamFromPlayerId(vulnerablePid);
  const responseTeam = getTeamFromPlayerId(responsePid);
  if (vulnerableTeam === responseTeam) return null;

  const vulnerableInfo = getVulnerableBallInfo(vulnerableShot);

  if (!vulnerableInfo) {
    const end = vulnerableShot?.resulting_ball_movement?.trajectory?.end?.location;
    const endX = num(end?.x);
    const endY = num(end?.y);
    const endZone = String(
      vulnerableShot?.resulting_ball_movement?.trajectory?.end?.zone ?? "",
    ).toLowerCase();
    const shotType = String(vulnerableShot?.shot_type ?? "").toLowerCase();
    const peakZ = num(vulnerableShot?.resulting_ball_movement?.trajectory?.peak?.z);
    const speed = num(vulnerableShot?.resulting_ball_movement?.speed);

    if (endX != null && endY != null) {
      const nearSidelineByFt = getNearSidelineByFt(endX);
      if (nearSidelineByFt <= 2.2 && endY >= 12 && endY <= 32) {
        console.log("ERNE vulnerable miss", {
          rallyIndex,
          vulnerableShotIndex,
          endX,
          endY,
          nearSidelineByFt,
          endZone,
          shotType,
          peakZ,
          speed,
        });
      }
    }

    return null;
  }
const ballContact = getBallContactPosForShot(responseShot);
  if (!ballContact) return null;


    const x = ballContact.x;
  const y = ballContact.y;

  const playerPos = getPlayerPosForShot(responseShot);



  const responseSide = sideFromX(x);
  if (responseSide !== vulnerableInfo.side) {
    const dxToVulnerable = Math.abs(x - vulnerableInfo.endX);
    if (dxToVulnerable > 3.5) return null;
  }
  const volley = isVolley(responseShot);

  const nearSidelineByFt = getNearSidelineByFt(x);
  const nearNetByFt = Math.abs(y - 22);

  const playerOutsideByFt = playerPos ? getOutsideByFt(playerPos.x) : 0;
  const ballOutsideByFt = getOutsideByFt(x);
  const outsideByFt = Math.max(playerOutsideByFt, ballOutsideByFt);

if (!volley) return null

let kind: ErneKind | null = null

if (outsideByFt > 0.05 && nearNetByFt <= 12) {
  kind = "erne"
}
else if (nearSidelineByFt <= 1.2 && nearNetByFt <= 12) {
  kind = "attempt"
}

if (!kind) return null
  const prev = getPrevSamePlayerContact(
    overlays,
    vulnerableShotIndex + 1,
    responsePid,
  );
  const lateralMoveFt = prev ? Math.abs(x - prev.x) : 0;
  const forwardMoveFt = prev ? Math.abs(y - prev.y) : 0;

  const shotType = String(responseShot?.shot_type ?? "").toLowerCase();
  const isPutaway = responseShot?.is_putaway === true;

  // Real Erne finish is usually an attacking volley, often a drive.
  const attackish =
    shotType === "drive" ||
    shotType === "smash" ||
    isPutaway;

  if (!attackish) return null;


  // This is the sequence part:
  // ball is vulnerable near a sideline, and attacker bursts mostly laterally to same side.
//   if (lateralMoveFt < 2.2) return null;
  if (forwardMoveFt > 6.5) return null;
  if (nearSidelineByFt > 0.75 && outsideByFt < 0.08) return null;


  if (outsideByFt >= 0.08 && nearNetByFt <= 11) {
    kind = "erne";
  } else if (
    nearSidelineByFt <= 0.75 &&
    nearNetByFt <= 11 &&
    lateralMoveFt >= 2.8
  ) {
    kind = "attempt";
  } else {
    return null;
  }
  return {
    rallyIndex,
    vulnerableShotIndex,
    responseShotIndex: vulnerableShotIndex + 1,
    vulnerableShotNumber: vulnerableShotIndex + 1,
    responseShotNumber: vulnerableShotIndex + 2,
    team: responseTeam,
    playerId: responsePid,
    kind,
    side: vulnerableInfo.side,
    shotIndex: vulnerableShotIndex + 1,
    shotNumber: vulnerableShotIndex + 2,
    shotStartMs: num(responseShot?.start_ms) ?? 0,
    shotEndMs: num(responseShot?.end_ms) ?? num(responseShot?.start_ms) ?? 0,
    contactX: x,
    contactY: y,
    outsideByFt,
    nearNetByFt,
    nearSidelineByFt,
    lateralMoveFt,
    forwardMoveFt,
    vulnerableBallX: vulnerableInfo.endX,
    vulnerableBallY: vulnerableInfo.endY,
    vulnerableBallZone: vulnerableInfo.endZone,
    isVolley: volley,
    isPutaway,
    shotType: responseShot?.shot_type,
    strokeType: responseShot?.stroke_type,
    strokeSide: responseShot?.stroke_side,
    notes: buildErneNotes({
      kind,
      x,
      y,
      nearSidelineByFt,
      outsideByFt,
      lateralMoveFt,
      forwardMoveFt,
      vulnerableInfo,
      shot: responseShot,
    }),
  };
}


export function computeErneEventsForRally(args: {
  rallyIndex: number;
  overlays: ShotNumberOverlay[];
}): ErneEvent[] {
  const out: ErneEvent[] = [];

  for (let i = 0; i < (args.overlays ?? []).length - 1; i += 1) {
    const ev = detectErneSequence({
      rallyIndex: args.rallyIndex,
      vulnerableShotIndex: i,
      overlays: args.overlays,
    });
    if (ev) out.push(ev);
  }

  return out;
}

export function buildErneClip(args: {
  event: ErneEvent;
  rallyStartMs?: number;
  rallyEndMs?: number;
  preMs?: number;
  postMs?: number;
}): ErneClip {
  const preMs = args.preMs ?? 900;
  const postMs = args.postMs ?? 900;

  // Start from the vulnerable-ball phase, not just the response contact.
  let clipStartMs = args.event.shotStartMs - preMs;
  let clipEndMs = args.event.shotEndMs + postMs;

  // Pull clip slightly earlier so the "ready to pounce" movement is visible.
  clipStartMs -= 700;

  if (typeof args.rallyStartMs === "number") {
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  }
  if (typeof args.rallyEndMs === "number") {
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);
  }

  if (clipEndMs - clipStartMs < 1400) {
    clipEndMs = clipStartMs + 1400;
  }

  return {
    key: `r${args.event.rallyIndex}_v${args.event.vulnerableShotIndex}_r${args.event.responseShotIndex}_${args.event.kind}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event: args.event,
    clipStartMs,
    clipEndMs,
  };
}


export function computeErneClipsFromRows(
  rows: KitchenArrivalRow[],
): ErneClip[] {
  const out: ErneClip[] = [];

  for (const row of rows ?? []) {
    const overlays = row?.shotNumberOverlays ?? [];
    const rallyIndex = row?.rallyIndex ?? 0;

    const events = computeErneEventsForRally({
      rallyIndex,
      overlays,
    });

    for (const event of events) {
      out.push(
        buildErneClip({
          event,
          rallyStartMs: row?.segmentStartMs,
          rallyEndMs: row?.segmentEndMs,
        }),
      );
    }
  }

  return out;
}

// new stuff above

function buildRallySegmentUrl(args: {
  packageId: string;
  rallyIndex: number;
  startMs: number;
  endMs: number;
  download?: 0 | 1;
}): string {
  return (
    `/api/rally-segment-video` +
    `?packageId=${encodeURIComponent(args.packageId)}` +
    `&rallyIndex=${encodeURIComponent(String(args.rallyIndex))}` +
    `&startMs=${encodeURIComponent(String(args.startMs))}` +
    `&endMs=${encodeURIComponent(String(args.endMs))}` +
    `&download=${encodeURIComponent(String(args.download ?? 0))}`
  );
}

function ErneBadge(props: { kind: ErneKind }) {
  const { kind } = props;
  const isErne = kind === "erne";

  return (
    <Box
      sx={{
        px: 0.9,
        py: 0.2,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1.2,
        bgcolor: isErne ? "rgba(30,136,229,0.14)" : "rgba(251,140,0,0.14)",
        color: isErne ? "rgba(13,71,161,0.95)" : "rgba(140,76,0,0.95)",
        border: isErne
          ? "1px solid rgba(30,136,229,0.28)"
          : "1px solid rgba(251,140,0,0.30)",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <SportsTennisIcon sx={{ fontSize: 13 }} />
      {isErne ? "Erne" : "Attempt"}
    </Box>
  );
}


export function ErnePopup(props: {
    insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  clipsThisRally: ErneClip[];
  clipsAll: ErneClip[];

  playerLabel?: (playerId: number) => string;
  teamLabel?: (team: 0 | 1) => string;

  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    clipsThisRally,
    clipsAll,
    activeRallyIndex,
    playerLabel,
    teamLabel,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
  } = props;

  const [pos, setPos] = React.useState({ x: 22, y: 86 });
  const [kindFilter, setKindFilter] = React.useState<FilterMode>("all");
  const [teamFilter, setTeamFilter] = React.useState<TeamFilter>("all");
  const [scope, setScope] = React.useState<"thisRally" | "allRallies">(
    "allRallies",
  );
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);


  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      setPos({
        x: d.startPosX + (e.clientX - d.startX),
        y: d.startPosY + (e.clientY - d.startY),
      });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const baseClips = scope === "thisRally" ? clipsThisRally : clipsAll;

  const filteredClips = React.useMemo(() => {
    return baseClips.filter((clip) => {
      const ev = clip.event;

      if (kindFilter === "ernes" && ev.kind !== "erne") return false;
      if (kindFilter === "attempts" && ev.kind !== "attempt") return false;
      if (teamFilter !== "all" && ev.team !== teamFilter) return false;

      return true;
    });
  }, [baseClips, kindFilter, teamFilter]);

//   React.useEffect(() => {
//     console.error("ERNE DEBUG", {
//       open,
//       clipsThisRallyCount: clipsThisRally.length,
//       clipsAllCount: clipsAll.length,
//       filteredCount: filteredClips.length,
//       firstFew: filteredClips.slice(0, 5),
//     });
//   }, [open, clipsThisRally, clipsAll, filteredClips]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(filteredClips[0]?.key ?? null);
  }, [open, scope, kindFilter, teamFilter, activeRallyIndex, filteredClips]);

  const selected =
    filteredClips.find((c) => c.key === selectedKey) ??
    filteredClips[0] ??
    null;

  const videoUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0,
    });
  }, [packageId, selected]);

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return rows.find((r) => r.rallyIndex === selected.event.rallyIndex) ?? null;
  }, [rows, selected]);

  const rallyRow = selectedRow;

  const clipRow = React.useMemo(() => {
    if (!selected || !selectedRow || !videoUrl) return null;

    return {
      ...selectedRow,
      videoUrl,
      segmentStartMs: selected.clipStartMs,
      segmentEndMs: selected.clipEndMs,
    };
  }, [selected, selectedRow, videoUrl]);
  //   const clipRow = React.useMemo(() => {
  //     if (!selected) return null;
  //     return rows.find((r) => r.rallyIndex === selected.event.rallyIndex) ?? null;
  //   }, [rows, selected]);

  //   const rallyRow = clipRow;

  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    selected?.event?.rallyIndex,
    selected?.event?.shotIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1320,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SportsTennisIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.35)" }} />
          <Box sx={{ fontWeight: 900, fontSize: 18 }}>Ernes</Box>
          <Box sx={{ fontSize: 12, opacity: 0.7 }}>
            {filteredClips.length} event{filteredClips.length === 1 ? "" : "s"}
          </Box>
        </Box>

        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 36, width: 36, height: 36, borderRadius: 999 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          gap: 1.2,
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          flexWrap: "wrap",
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope}
          onChange={(_, v) => v && setScope(v)}
        >
          <ToggleButton value="allRallies">All rallies</ToggleButton>
          <ToggleButton value="thisRally">This rally</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={kindFilter}
          onChange={(_, v) => v && setKindFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="ernes">Ernes</ToggleButton>
          <ToggleButton value="attempts">Attempts</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={String(teamFilter)}
          onChange={(_, v) => {
            if (v === "all") setTeamFilter("all");
            else if (v === "0") setTeamFilter(0);
            else if (v === "1") setTeamFilter(1);
          }}
        >
          <ToggleButton value="all">All teams</ToggleButton>
          <ToggleButton value="0">
            {teamLabel ? teamLabel(0) : "Team 0"}
          </ToggleButton>
          <ToggleButton value="1">
            {teamLabel ? teamLabel(1) : "Team 1"}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          sx={{
            width: 440,
            minWidth: 440,
            borderRight: "1px solid rgba(0,0,0,0.10)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Rally</TableCell>
                  <TableCell>Shot</TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell align="right">Outside</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClips.map((clip) => {
                  const ev = clip.event;
                  const isSel = clip.key === selected?.key;

                  return (
                    <TableRow
                      key={clip.key}
                      hover
                      selected={isSel}
                      onClick={() => setSelectedKey(clip.key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <ErneBadge kind={ev.kind} />
                      </TableCell>
                      <TableCell>{ev.rallyIndex + 1}</TableCell>
                      <TableCell>{ev.responseShotNumber}</TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 700 }}>
                          {playerLabel
                            ? playerLabel(ev.playerId)
                            : `P${ev.playerId}`}
                        </Box>
                        <Box sx={{ fontSize: 11, opacity: 0.65 }}>
                          {teamLabel ? teamLabel(ev.team) : `Team ${ev.team}`} -{" "}
                          {ev.side}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {ev.outsideByFt > 0 ? ev.outsideByFt.toFixed(1) : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {selected ? (
            <Box
              sx={{
                p: 1.2,
                borderTop: "1px solid rgba(0,0,0,0.10)",
                bgcolor: "rgba(0,0,0,0.02)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}
              >
                <ErneBadge kind={selected.event.kind} />
                <Box sx={{ fontWeight: 900 }}>
                  Rally {selected.event.rallyIndex + 1} - Shot{" "}
                  {selected.event.vulnerableShotNumber}
                </Box>
              </Box>

<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
  <Box>
    Player: {playerLabel ? playerLabel(selected.event.playerId) : `P${selected.event.playerId}`}
  </Box>
  <Box>
    Vulnerable ball: x={selected.event.vulnerableBallX.toFixed(1)}, y={selected.event.vulnerableBallY.toFixed(1)}
    {selected.event.vulnerableBallZone ? ` (${selected.event.vulnerableBallZone})` : ""}
  </Box>
  <Box>
    Response contact: x={selected.event.contactX.toFixed(1)}, y={selected.event.contactY.toFixed(1)}
  </Box>
  <Box>
    Near net: {selected.event.nearNetByFt.toFixed(1)} ft
  </Box>
  <Box>
    Near sideline: {selected.event.nearSidelineByFt.toFixed(1)} ft
  </Box>
  <Box>
    Outside sideline: {selected.event.outsideByFt.toFixed(1)} ft
  </Box>
  <Box>
    lateral={selected.event.lateralMoveFt.toFixed(1)} ft, forward={selected.event.forwardMoveFt.toFixed(1)} ft
  </Box>
  <Box>
    {selected.event.strokeSide ?? "-"} {selected.event.strokeType ?? "-"} {selected.event.shotType ?? ""}
  </Box>
</Box>

              <Box
                sx={{ mt: 0.9, display: "flex", flexWrap: "wrap", gap: 0.6 }}
              >
                {selected.event.notes.map((note, i) => (
                  <Box
                    key={`${selected.key}_${i}`}
                    sx={{
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 999,
                      fontSize: 11,
                      bgcolor: "rgba(0,0,0,0.05)",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {note}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "black",
          }}
        >
          {clipRow && selected && videoUrl ? (
            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
              <ActiveRallyVideo
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                aiContextLabel={"Ernes"}
                focusedFilter={null}
                packageId={packageId}
                row={clipRow}
                rows={rows}
                activeShotNumber={selected.event.responseShotNumber}
                showCondensedTimeline={false}
                fontColor={"white"}
                controlsPlacement={"left"}
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode={"split"}
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={false}
                isInFullPopup={false}
                seedPlayback={{
                  timeSec: 0,
                  paused: false,
                  nonce: seedNonce,
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  px: 1,
                  py: 0.6,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.65)",
                  color: "white",
                  fontSize: 12,
                  lineHeight: 1.1,
                  maxWidth: "calc(100% - 20px)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ErneBadge kind={selected.event.kind} />
                <Box sx={{ fontWeight: 900 }}>
                  Rally {selected.event.rallyIndex + 1} - Shot{" "}
                  {selected.event.responseShotNumber}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                opacity: 0.75,
              }}
            >
              No Erne event selected
            </Box>
          )}

          {rallyRow && selected ? (
            <Box
              sx={{
                height: 280,
                borderTop: "1px solid rgba(255,255,255,0.10)",
                position: "relative",
              }}
            >
              <ActiveRallyVideo
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                aiContextLabel={"Ernes Full Rally"}
                focusedFilter={null}
                packageId={packageId}
                row={rallyRow}
                rows={rows}
                activeShotNumber={selected.event.shotNumber}
                showCondensedTimeline={true}
                fontColor={"white"}
                controlsPlacement={"left"}
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={false}
                aiDrawerMode={"split"}
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={false}
                isInFullPopup={false}
                seedPlayback={{
                  timeSec: 0,
                  paused: false,
                  nonce: seedNonce + 1000,
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "white",
                  fontSize: 12,
                  pointerEvents: "none",
                }}
              >
                <Box sx={{ fontWeight: 900, display: "inline-block", mr: 0.5 }}>
                  Full rally
                </Box>
                <Box sx={{ opacity: 0.85, display: "inline-block" }}>
                  Rally {selected.event.rallyIndex + 1}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
}
